import React, { useEffect, useState, useCallback } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { enqueueSnackbar } from 'notistack';
import { ExportHubAPI, ExportDatabase } from 'api/ExportHubAPI';
import DatabaseCard from './DatabaseCard';

interface Props {
  groupId: string | undefined;
  onOpenDatabase: (db: ExportDatabase) => void;
  onRequestCreate: () => void;
  /** Bump to force a reload (e.g. after returning from elsewhere). */
  reloadKey?: number;
}

const SKELETON_COUNT = 6;

const DatabasesPanel: React.FC<Props> = ({
  groupId,
  onOpenDatabase,
  onRequestCreate,
  reloadKey,
}) => {
  const [databases, setDatabases] = useState<ExportDatabase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!groupId) {
      setDatabases([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const data = await ExportHubAPI.getDatabases(groupId);
      setDatabases(data);
    } catch (err) {
      console.error('Failed to load databases:', err);
      setError(true);
      enqueueSnackbar('Failed to load databases.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const handleDelete = useCallback(
    async (db: ExportDatabase): Promise<void> => {
      if (!groupId) return;
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        `Delete "${db.name}"?\n\nThis permanently deletes the database and its ` +
          `${db.product_count} product(s) along with their screenings, ` +
          `classification results, and license links. This cannot be undone.`,
      );
      if (!confirmed) return;
      try {
        await ExportHubAPI.deleteDatabase(db.id, groupId);
        enqueueSnackbar(`Deleted "${db.name}".`, { variant: 'success' });
        setDatabases((prev) => prev.filter((d) => d.id !== db.id));
      } catch (err) {
        console.error('Failed to delete database:', err);
        enqueueSnackbar('Failed to delete database.', { variant: 'error' });
      }
    },
    [groupId],
  );

  // Section sub-header — always rendered beneath the page tabs so the tab
  // heading stays in sync as the user navigates (matches the other tabs).
  const sectionHeader = (
    <div className='export-hub-section-header'>
      <div>
        <h2 className='export-hub-section-title'>Databases</h2>
        <p className='export-hub-section-sub'>
          Scoped workspaces for products, screenings, and licenses. Open one to
          drill in.
        </p>
      </div>
      <div className='export-hub-section-actions'>
        <button className='btn-primary' type='button' onClick={onRequestCreate}>
          <AddIcon style={{ fontSize: 18 }} /> Create Database
        </button>
      </div>
    </div>
  );

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div className='export-hub-panel'>
        <div className='eh-db-grid'>
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div key={i} className='eh-db-card eh-db-card-skeleton'>
              <div className='eh-skel eh-skel-icon' />
              <div className='eh-skel eh-skel-line' style={{ width: '60%' }} />
              <div className='eh-skel eh-skel-line' style={{ width: '90%' }} />
              <div className='eh-skel eh-skel-kpis' />
            </div>
          ))}
        </div>
      </div>
    );
  } else if (error) {
    body = (
      <div className='export-hub-panel'>
        <div className='export-hub-empty'>
          <div className='export-hub-empty-title'>Couldn&rsquo;t load databases</div>
          <div className='export-hub-empty-sub'>
            Something went wrong fetching your databases.
          </div>
          <button
            className='btn-outline'
            style={{ marginTop: 12 }}
            onClick={load}
            type='button'
          >
            Retry
          </button>
        </div>
      </div>
    );
  } else if (databases.length === 0) {
    body = (
      <div className='export-hub-panel'>
        <div className='export-hub-empty'>
          <div className='export-hub-empty-title'>No databases yet</div>
          <div className='export-hub-empty-sub'>
            Create your first database to start organizing export products,
            screenings, and licenses.
          </div>
          <button
            className='btn-primary'
            style={{ marginTop: 16 }}
            onClick={onRequestCreate}
            type='button'
          >
            <AddIcon style={{ fontSize: 18 }} /> Create Database
          </button>
        </div>
      </div>
    );
  } else {
    body = (
      <div className='export-hub-panel'>
        <div className='eh-db-grid'>
          {databases.map((db) => (
            <DatabaseCard
              key={db.id}
              database={db}
              onOpen={onOpenDatabase}
              onDelete={handleDelete}
            />
          ))}
          <button
            className='eh-db-create-tile'
            onClick={onRequestCreate}
            type='button'
          >
            <AddIcon style={{ fontSize: 24 }} />
            <span>Create Database</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {sectionHeader}
      {body}
    </>
  );
};

export default DatabasesPanel;
